import { useEffect, useState, type FormEvent } from 'react'
import { PortalDisclosure } from '@gamecodex/portal-ui'
import { CHANNELS, HISTORY, REPLIES_ARE_DISABLED_REASON } from './channels'
import { useSend } from './useSend'
import { PrivacyPolicy } from './legal/PrivacyPolicy'
import { Terms } from './legal/Terms'

function currentPath() {
  return typeof window !== 'undefined' ? window.location.pathname : '/'
}

/**
 * mythzord.com
 *
 * Server-shaped on purpose: a channel rail, a message pane, a composer that
 * never quite lands. It has to look like somewhere people are talking for the
 * fact that Sergeant Chill reads everything before it arrives to be funny.
 *
 * /privacy and /terms are plain pathname routes (no router lib needed for two
 * static pages, same pattern as dicetok); netlify.toml's SPA catch-all serves
 * index.html for both.
 */
export default function App() {
  const [path, setPath] = useState(currentPath)
  const navigate = (to: string) => {
    window.history.pushState({}, '', to)
    setPath(to)
  }
  useEffect(() => {
    const onPopState = () => setPath(currentPath())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const { pending, verdict, degraded, error, send, dismiss } = useSend()
  const [active, setActive] = useState(CHANNELS[0].id)
  const [draft, setDraft] = useState('')

  const channel = CHANNELS.find((c) => c.id === active) ?? CHANNELS[0]
  const messages = HISTORY[channel.id] ?? []

  const onSend = (e: FormEvent) => {
    e.preventDefault()
    void send(channel.id, draft)
  }

  const switchChannel = (id: string) => {
    setActive(id)
    dismiss()
  }

  if (path === '/privacy') return <PrivacyPolicy onNavigate={navigate} />
  if (path === '/terms') return <Terms onNavigate={navigate} />

  return (
    <div className="app">
      <nav className="channels">
        <div className="server">
          <span className="server-icon">SZ</span>
          <span className="server-name">Session Zero</span>
        </div>
        <div className="channel-list">
          {CHANNELS.map((c) => (
            <button
              key={c.id}
              type="button"
              className={c.id === active ? 'channel active' : 'channel'}
              onClick={() => switchChannel(c.id)}
              title={c.topic}
            >
              <span className="hash">#</span>
              {c.name}
              {c.locked && <span className="lock">🔒</span>}
            </button>
          ))}
        </div>
        <PortalDisclosure
          portal="Mythzord"
          detail="Messages shown here are fictional. Nothing you send is stored."
          className="disclosure"
        />
        <nav className="legalLinks">
          <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy') }}>
            Privacy Policy
          </a>
          <a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms') }}>
            Terms &amp; Conditions
          </a>
        </nav>
      </nav>

      <main className="pane">
        <header className="pane-top">
          <span className="hash">#</span>
          <span className="pane-name">{channel.name}</span>
          <span className="pane-topic">{channel.topic}</span>
        </header>

        <section className="messages">
          {messages.map((m) => (
            <article key={m.id} className={m.isMod ? 'msg mod' : 'msg'}>
              <div className="avatar" aria-hidden="true">
                {m.author.slice(0, 1).toUpperCase()}
              </div>
              <div className="content">
                <div className="meta">
                  <span className="author">{m.author}</span>
                  {m.isMod && <span className="badge">BOT</span>}
                  <span className="age">{m.pinned ? 'pinned' : m.age}</span>
                </div>
                <p>{m.body}</p>
              </div>
            </article>
          ))}

          {verdict && (
            <article className="msg mod verdict-msg" role="status">
              <div className="avatar" aria-hidden="true">
                S
              </div>
              <div className="content">
                <div className="meta">
                  <span className="author">Sergeant Chill</span>
                  <span className="badge">BOT</span>
                  <span className="age">now</span>
                </div>
                <p>{verdict}</p>
                <div className="row">
                  <button type="button" onClick={dismiss}>
                    Acknowledge
                  </button>
                  {degraded && <span className="hint">Reviewed under standard guidance.</span>}
                </div>
              </div>
            </article>
          )}

          {error && (
            <p className="err" role="alert">
              {error}
            </p>
          )}
        </section>

        <form className="composer" onSubmit={onSend}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`Message #${channel.name}`}
            maxLength={2000}
            aria-label={`Message #${channel.name}`}
            title={REPLIES_ARE_DISABLED_REASON}
          />
          <button type="submit" disabled={pending || !draft.trim()}>
            {pending ? 'Reviewing…' : 'Send'}
          </button>
        </form>
      </main>
    </div>
  )
}
